/**
 * Supported UI languages.
 *
 * Persian (fa) is a right-to-left language; English and Russian are
 * left-to-right. Every user must choose a language on first launch.
 */

export const LANGUAGES = ['en', 'fa', 'ru'] as const;

export type Language = (typeof LANGUAGES)[number];

export const DEFAULT_LANGUAGE: Language = 'en';

export type TextDirection = 'ltr' | 'rtl';

/** Human-facing metadata for each language (used in the language picker). */
export interface LanguageMeta {
  code: Language;
  /** Name written in the language itself. */
  nativeName: string;
  /** Name written in English. */
  englishName: string;
  direction: TextDirection;
  /** Emoji flag, purely decorative. */
  flag: string;
}

export const LANGUAGE_META: Record<Language, LanguageMeta> = {
  en: {
    code: 'en',
    nativeName: 'English',
    englishName: 'English',
    direction: 'ltr',
    flag: '🇬🇧',
  },
  fa: {
    code: 'fa',
    nativeName: 'فارسی',
    englishName: 'Persian',
    direction: 'rtl',
    flag: '🇮🇷',
  },
  ru: {
    code: 'ru',
    nativeName: 'Русский',
    englishName: 'Russian',
    direction: 'ltr',
    flag: '🇷🇺',
  },
};

export function isLanguage(value: unknown): value is Language {
  return typeof value === 'string' && (LANGUAGES as readonly string[]).includes(value);
}

export function directionFor(language: Language): TextDirection {
  return LANGUAGE_META[language].direction;
}
